import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import Reveal from "../components/Reveal";
import Gallery from "../components/Gallery";
import { ArrowLeft } from "lucide-react";

function DetailMeta({ item }) {
    const rows = [
        ["Anno", item.year],
        ["Categoria", item.category],
        ["Broadcaster", item.broadcaster],
        ["Partner", item.partner],
        ["Location", item.location],
    ].filter(([, v]) => v);
    if (!rows.length) return null;
    return (
        <div className="border-t border-white/10 divide-y divide-white/10">
            {rows.map(([k, v]) => (
                <div key={k} className="flex justify-between py-3 text-sm">
                    <span className="kicker text-white/50">{k}</span>
                    <span className="text-white/90 text-right">{v}</span>
                </div>
            ))}
        </div>
    );
}

export default function DetailPage({ kind, backTo, backLabel }) {
    const { slug } = useParams();
    const [item, setItem] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setItem(null);
        setErr(null);
        api.get(`/${kind}/slug/${slug}`)
            .then((r) => setItem(r.data))
            .catch((e) => setErr(e.response?.status === 404 ? "Contenuto non trovato" : "Errore"));
    }, [kind, slug]);

    if (err) {
        return (
            <div className="container-nm pt-40 pb-32 text-center" data-testid="detail-error">
                <h1 className="font-display text-5xl font-extrabold mb-6">{err}</h1>
                <Link to={backTo} className="btn-ghost text-white">
                    ← {backLabel}
                </Link>
            </div>
        );
    }
    if (!item) {
        return <div className="container-nm pt-40 pb-32 text-white/50" data-testid="detail-loading">Caricamento…</div>;
    }

    const galleryImages = (item.images && item.images.length ? item.images : [item.cover_image]).filter(Boolean);

    return (
        <div data-testid="detail-page">
            {/* HERO */}
            <section className="relative h-[80vh] min-h-[540px] flex items-end overflow-hidden">
                {item.cover_image && (
                    <img
                        src={mediaUrl(item.cover_image)}
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black" />
                <div className="container-nm relative pb-14 md:pb-20 pt-32 w-full">
                    <Link to={backTo} className="inline-flex items-center gap-2 kicker text-white/70 hover:text-white mb-8" data-testid="detail-back">
                        <ArrowLeft size={14} /> {backLabel}
                    </Link>
                    <div className="kicker mb-5">
                        {item.category || item.broadcaster || "N&M"}
                        {item.year ? ` · ${item.year}` : ""}
                    </div>
                    <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[6rem] font-extrabold leading-[0.95] tracking-[-0.035em]">
                        {item.title}
                    </h1>
                    {item.subtitle && (
                        <p className="mt-6 text-xl md:text-2xl text-white/80 max-w-3xl leading-snug">
                            {item.subtitle}
                        </p>
                    )}
                </div>
            </section>

            {/* BODY */}
            <section className="container-nm py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
                <Reveal className="md:col-span-8">
                    {item.description_short && (
                        <p className="font-editorial text-2xl md:text-3xl leading-snug text-white/90 mb-8">
                            {item.description_short}
                        </p>
                    )}
                    {item.description_full && (
                        <div className="text-white/75 text-lg leading-relaxed whitespace-pre-line">
                            {item.description_full}
                        </div>
                    )}
                    {!!item.tags?.length && (
                        <div className="mt-10 flex flex-wrap gap-2">
                            {item.tags.map((t) => (
                                <span
                                    key={t}
                                    className="px-3 py-1 text-xs uppercase tracking-[0.16em] border border-white/20"
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    )}
                </Reveal>
                <Reveal delay={150} className="md:col-span-4 md:col-start-9">
                    <DetailMeta item={item} />
                    {item.extra && Object.keys(item.extra).length > 0 && (
                        <div className="mt-6 border-t border-white/10 divide-y divide-white/10">
                            {Object.entries(item.extra).map(([k, v]) => (
                                <div key={k} className="flex justify-between py-3 text-sm">
                                    <span className="kicker text-white/50">{k}</span>
                                    <span className="text-white/90 text-right">{String(v)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Reveal>
            </section>

            {/* GALLERY */}
            {galleryImages.length > 1 && (
                <section className="container-nm pb-24 md:pb-32">
                    <Reveal>
                        <div className="kicker mb-5">Gallery</div>
                        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-[-0.03em] mb-10">
                            Dentro il progetto
                        </h2>
                    </Reveal>
                    <Gallery images={galleryImages} />
                </section>
            )}
        </div>
    );
}
